// DLG-129 Add / Edit Location (legacy pages/shared/accounts/_partials/LocationsItem.vue; field note
// backoffice.accounts.detail), one dialog for Account Detail's Create Location and location menu Edit and for Location
// Detail's Edit (A40 (b), Andrew 2026-10-12: one schema and rule set; ledger L325 for B191). Legacy's sections, copy and
// messages: Details (Location Name with its hint, Email, Phone, Tax Rate, Time Zone), Location ACH (Routing Number,
// Account Number), Location Address (Address, City, State, Zip, Contact First Name, Contact Last Name).
// Changes, each ledgered: Edit says "Edit the location's name …" (B187, L321); the ACH numbers are optional and
// write-only, the saved ones named by their last four (A39 (b)); a time zone change asks first, naming its effect.
// Not here: legacy's Admin Only "Live Update Channel" (the OTA plan, DLG-137).
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, ScaledField, SECTION_HEADING, SelectField, TextField, useToast } from '@radish/ui-web';
import { unwrap, useApi, useLocationQuery, type CallFailure } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';
import { rateFromTenThousandths, tenThousandthsOf } from '../../lib/rates.ts';
import { refreshSession } from '../../session/session.ts';
import { phoneText, zoneLabel } from '../branding/LocationInfoPage.tsx';
import { ConfirmDialog } from '../users/UsersPage.tsx';
import { emailErrors } from '../../lib/email.ts';

const ZONES = ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'Pacific/Honolulu'] as const;
type Zone = (typeof ZONES)[number];

const ADD_INTRO = 'Give the new location a name and enter the business information such as the tax rate, physical address, and contact information.';
const EDIT_INTRO = "Edit the location's name and business information such as the tax rate, physical address, and contact information.";

interface Form {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly tax: bigint;
  readonly timezone: Zone | null;
  readonly routing: string;
  readonly account: string;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly contactFirstName: string;
  readonly contactLastName: string;
}

const BLANK: Form = {
  name: '', email: '', phone: '', tax: 0n, timezone: null, routing: '', account: '', addressLine1: '', city: '', state: '', zip: '', contactFirstName: '', contactLastName: '',
};

type Errors = Partial<Record<keyof Form, string>>;

/** A40 (b): one rule set, with legacy's messages. */
function check(f: Form): Errors {
  const email = f.email.trim();
  const phone = f.phone.replace(/\D/g, '');
  const required = (key: keyof Form, message: string): Errors => (String(f[key]).trim() === '' ? { [key]: message } : {});
  const digits = (key: 'routing' | 'account', message: string): Errors => (f[key].trim() !== '' && !/^[0-9]{4,34}$/.test(f[key].trim()) ? { [key]: message } : {});
  return {
    ...required('name', 'Location Name is required'),
    ...emailErrors(email),
    ...(phone === '' ? { phone: 'Phone is required' } : phone.length !== 10 ? { phone: 'Phone number must be 10 digits' } : {}),
    ...(f.timezone === null ? { timezone: 'Time Zone is required' } : {}),
    ...digits('routing', 'Please type the Routing Number'),
    ...digits('account', 'Please type the Account Number'),
    ...required('addressLine1', 'Address is required'),
    ...required('city', 'City is required'),
    ...required('state', 'State is required'),
    ...required('zip', 'Zip is required'),
    ...required('contactFirstName', 'Contact First Name is required'),
    ...required('contactLastName', 'Contact Last Name is required'),
  };
}

// Legacy UiSectionHeading: a bold title with no rule under it (lane V r6, Add / Edit Location).
const Heading = ({ title }: { readonly title: string }) => <h3 data-testid="location-dialog-heading" className={`mt-2 ${SECTION_HEADING}`}>{title}</h3>;

export function LocationDialog({ open, accountId, locationId, onClose }: {
  readonly open: boolean;
  /** The account the staff page acts in (its query cache key). */
  readonly accountId: string;
  /** The location to edit, or null to add one. */
  readonly locationId: string | null;
  readonly onClose: () => void;
}) {
  const toast = useToast();
  const { client, environment } = useApi();
  const queryClient = useQueryClient();
  const editing = locationId !== null;
  const loaded = useLocationQuery(accountId, 'tenancy.location', { locationId }, (c) => c.queries.tenancy.location({ locationId: locationId as never }), open && editing).data;
  const [form, setForm] = useState<Form>(BLANK);
  const [errors, setErrors] = useState<Errors>({});
  const [confirmZone, setConfirmZone] = useState(false);
  // An add keeps one location id per dialog, so a retried attempt replays instead of adding a second location.
  const [newId, setNewId] = useState(() => uuidv7());

  useEffect(() => {
    if (!open) return;
    if (!editing) setForm(BLANK);
    else if (loaded !== undefined) {
      setForm({
        name: loaded.name, email: loaded.email, phone: loaded.phone, tax: tenThousandthsOf(loaded.taxRate),
        timezone: (ZONES as readonly string[]).includes(loaded.timezone) ? (loaded.timezone as Zone) : null, routing: '', account: '',
        addressLine1: loaded.addressLine1, city: loaded.city, state: loaded.state, zip: loaded.zip, contactFirstName: loaded.contactFirstName, contactLastName: loaded.contactLastName,
      });
    }
  }, [open, editing, loaded]);

  const save = useMutation<unknown, CallFailure, Form>({
    mutationFn: (f) => {
      const commandId = uuidv7();
      const details = {
        name: f.name.trim(), email: f.email.trim().toLowerCase(), phone: f.phone.replace(/\D/g, ''), taxRate: rateFromTenThousandths(f.tax), timezone: f.timezone ?? 'America/Chicago',
        addressLine1: f.addressLine1.trim(), city: f.city.trim(), state: f.state.trim(), zip: f.zip.trim(), contactFirstName: f.contactFirstName.trim(), contactLastName: f.contactLastName.trim(),
        ...(f.routing.trim() === '' ? {} : { achRoutingNumber: f.routing.trim() }),
        ...(f.account.trim() === '' ? {} : { achAccountNumber: f.account.trim() }),
      };
      return editing
        ? unwrap(() => client.commands.tenancy.updateLocation({ ...details, locationId: locationId as never }, { commandId }))
        : unwrap(() => client.commands.tenancy.createLocation({ ...details, locationId: newId as never }, { commandId }));
    },
  });

  const close = () => {
    setForm(BLANK);
    setErrors({});
    setConfirmZone(false);
    save.reset();
    onClose();
  };

  const send = () => {
    setConfirmZone(false);
    save.mutate(form, {
      onSuccess: async () => {
        toast.success(editing ? 'Location updated successfully' : 'Location created successfully');
        if (!editing) setNewId(uuidv7());
        await queryClient.invalidateQueries({ queryKey: [environment.profile, accountId] });
        // The staff pages list the session's locations of the account: a refresh brings the new or renamed one.
        await refreshSession();
        close();
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const submit = () => {
    const next = check(form);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (editing && loaded !== undefined && form.timezone !== loaded.timezone) setConfirmZone(true);
    else send();
  };

  const set = (key: keyof Form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key: Exclude<keyof Form, 'tax' | 'timezone'>, label: string, hint?: string) => (
    <TextField label={label} value={form[key]} onValueChange={set(key)} error={errors[key]} {...(hint === undefined ? {} : { hint })} />
  );
  // Legacy LocationsItem `validFields`: Create / Update stay disabled until the name, phone and full address are in.
  const filled = [form.name, form.phone, form.addressLine1, form.city, form.state, form.zip].every((v) => v.trim() !== '');
  const lastFour = (n: string | null | undefined) => (n === null || n === undefined ? undefined : `Number ending in ${n} is saved. Leave blank to keep it.`);

  return (
    <>
      <Dialog
        open={open && !confirmZone}
        onOpenChange={(o) => { if (!o) close(); }}
        title={editing ? 'Edit Location' : 'Add Location'}
        phoneFooterFill
        testId="location-dialog"
        footer={
          <>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={submit} loading={save.isPending} disabled={!filled || (editing && loaded === undefined)}>{editing ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <form className="flex flex-col gap-4" autoComplete="off" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <p className="text-[14px] text-text-3">{editing ? EDIT_INTRO : ADD_INTRO}</p>
          <Heading title="Details" />
          {text('name', 'Location Name', 'Note that the location name appears on your reports and customer receipts.')}
          <TextField label="Email" type="email" value={form.email} onValueChange={set('email')} error={errors.email} />
          <TextField label="Phone" inputMode="tel" value={phoneText(form.phone.replace(/\D/g, ''))} onValueChange={(v) => set('phone')(v.replace(/\D/g, '').slice(0, 10))} error={errors.phone} />
          <ScaledField label="Tax Rate" suffix="%" decimals={4} maxDigits={7} value={form.tax} onValueChange={(v) => setForm((f) => ({ ...f, tax: v ?? 0n }))} />
          <SelectField
            label="Time Zone"
            value={form.timezone}
            options={ZONES.map((z) => ({ value: z, label: zoneLabel(z) }))}
            onValueChange={(z) => setForm((f) => ({ ...f, timezone: z }))}
            error={errors.timezone}
            testId="location-timezone"
          />
          <Heading title="Location ACH" />
          {/* Legacy's rows are q-col-gutter-sm: each column pads 8 px top and left. Its ACH fields are col-sm-3 col-md-6, a
              quarter of the row each from 600 to 1023 with the right half empty (legacy bug B1240); Next keeps them halves from
              600 (B1216). The ACH row sits straight in the q-gutter-md form, whose child margin overrides the row's -8 px pull,
              so its fields sit 8 px in and 8 px down from the other fields, and 8 px apart when stacked (B1342). */}
          <div data-testid="location-ach-row" className="grid grid-cols-1 gap-y-2 pt-2 *:pl-2 min-[600px]:grid-cols-2">
            <TextField label="Routing Number" value={form.routing} onValueChange={set('routing')} error={errors.routing} hint={lastFour(loaded?.achRoutingLastFour)} autoComplete="off" />
            <TextField label="Account Number" value={form.account} onValueChange={set('account')} error={errors.account} hint={lastFour(loaded?.achAccountLastFour)} autoComplete="off" />
          </div>
          <Heading title="Location Address" />
          {text('addressLine1', 'Address')}
          {/* City, State and Zip are a q-col-gutter-sm row inside a plain div, so the row's -8 px pull applies: columns are
              a half and two quarters of the form plus 8, each padded 8 px left, and 8 px apart when stacked (B1342). */}
          <div data-testid="location-address-row" className="-ml-2 grid grid-cols-1 gap-y-2 *:pl-2 min-[600px]:grid-cols-[2fr_1fr_1fr]">
            {text('city', 'City')}
            {text('state', 'State')}
            {text('zip', 'Zip')}
          </div>
          {/* Legacy's contact names are col-sm-6 straight in the q-form, outside any row, so Quasar never sizes them: stacked
              full width at every width (B1216). */}
          {text('contactFirstName', 'Contact First Name')}
          {text('contactLastName', 'Contact Last Name')}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>
      </Dialog>
      <ConfirmDialog
        open={confirmZone}
        title="Change Time Zone"
        message={`Change the time zone to ${form.timezone === null ? '' : zoneLabel(form.timezone)}? Business days from the next day's start follow the new zone; earlier days keep theirs.`}
        action="Change Time Zone"
        busy={save.isPending}
        onClose={() => setConfirmZone(false)}
        onConfirm={send}
      />
    </>
  );
}
